"use client";

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, DetailedHTMLProps } from "react";
import type { ButtonProps as AriaButtonProps, LinkProps as AriaLinkProps } from "react-aria-components";
import { Button as AriaButton, Link as AriaLink } from "react-aria-components";
import { cx, sortCx } from "@/lib/utils/cx";
import { AppleLogo, DiscordLogo, DribbleLogo, EmailLogo, FacebookLogo, FigmaLogo, FigmaLogoOutlined, GoogleLogo, TwitterLogo } from "./social-logos";

export const styles = sortCx({
    common: {
        root: "group disabled:stroke-fg-disabled disabled:text-fg-disabled disabled:*:text-fg-disabled relative inline-flex h-max cursor-pointer items-center justify-center font-semibold whitespace-nowrap outline-focus-ring transition duration-150 ease-linear before:absolute focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed",
        icon: "pointer-events-none shrink-0 transition-inherit-all",
    },

    sizes: {
        md: {
            root: "gap-2.5 rounded-lg px-3.5 py-2.5 text-sm before:rounded-[7px] data-icon-only:p-2.5",
            icon: "size-5",
        },
        lg: {
            root: "gap-3 rounded-lg px-4 py-2.5 text-sm sm:text-base before:rounded-[7px] data-icon-only:p-3",
            icon: "size-5",
        },
    },

    colors: {
        google: {
            root: "bg-white text-[#344054] shadow-xs border border-[#D0D5DD] hover:bg-gray-50 hover:text-[#1D2939] dark:bg-zinc-900 dark:text-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800",
            icon: "",
        },
        gray: {
            root: "bg-primary text-secondary shadow-xs ring-1 ring-border hover:bg-primary_hover hover:text-secondary_hover",
            icon: "text-muted-foreground group-hover:text-foreground",
        },
        black: {
            root: "bg-black text-white shadow-xs border border-transparent hover:bg-zinc-900",
            icon: "text-white",
        },
        facebook: {
            root: "bg-[#1877F2] text-white shadow-xs border border-transparent hover:bg-[#0C63D4]",
            icon: "text-white",
        },
        dribble: {
            root: "bg-[#EA4C89] text-white shadow-xs border border-transparent hover:bg-[#E62872]",
            icon: "text-white",
        },
        discord: {
            root: "bg-[#5865F2] text-white shadow-xs border border-transparent hover:bg-[#4752C4]",
            icon: "text-white",
        },
        email: {
            root: "bg-[#101828] text-white shadow-xs border border-transparent hover:bg-[#1D2939] dark:bg-zinc-800 dark:hover:bg-zinc-700",
            icon: "text-white",
        },
    },
});

interface CommonProps {
    social: "google" | "facebook" | "apple" | "twitter" | "figma" | "dribble" | "discord" | "email";
    disabled?: boolean;
    theme?: "brand" | "color" | "gray";
    size?: keyof typeof styles.sizes;
}

interface ButtonProps extends CommonProps, DetailedHTMLProps<Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "slot">, HTMLButtonElement> {
    slot?: AriaButtonProps["slot"];
}

interface LinkProps extends CommonProps, DetailedHTMLProps<Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "color">, HTMLAnchorElement> {
    /** Options for the configured client side router. */
    routerOptions?: AriaLinkProps["routerOptions"];
}

export type SocialButtonProps = ButtonProps | LinkProps;

export const SocialButton = ({ size = "lg", theme = "brand", social, className, children, disabled, ...otherProps }: SocialButtonProps) => {
    const href = "href" in otherProps ? otherProps.href : undefined;
    const Component = href ? AriaLink : AriaButton;

    const isIconOnly = !children;

    const socialToColor = {
        google: "google",
        facebook: "facebook",
        apple: "black",
        twitter: "black",
        figma: "black",
        dribble: "dribble",
        discord: "discord",
        email: "email",
    } as const;

    const colorStyles = theme === "brand" ? styles.colors[socialToColor[social]] : styles.colors.gray;

    const logos = {
        google: GoogleLogo,
        facebook: FacebookLogo,
        apple: AppleLogo,
        twitter: TwitterLogo,
        figma: theme === "gray" ? FigmaLogoOutlined : FigmaLogo,
        dribble: DribbleLogo,
        discord: DiscordLogo,
        email: EmailLogo,
    };

    const Logo = logos[social];

    let props = {};

    if (href) {
        props = {
            ...otherProps,

            href: disabled ? undefined : href,

            // Since anchor elements do not support the `disabled` attribute and state,
            // we need to specify `data-rac` and `data-disabled` in order to be able
            // to use the `disabled:` selector in classes.
            ...(disabled ? { "data-rac": true, "data-disabled": true } : {}),
        };
    } else {
        props = {
            ...otherProps,

            type: otherProps.type || "button",
            isDisabled: disabled,
        };
    }

    return (
        <Component
            isDisabled={disabled}
            {...props}
            data-icon-only={isIconOnly ? true : undefined}
            className={cx(styles.common.root, styles.sizes[size].root, colorStyles.root, className)}
        >
            <Logo
                className={cx(
                    styles.common.icon,
                    styles.sizes[size].icon,
                    theme === "gray"
                        ? colorStyles.icon
                        : theme === "brand" && (social === "facebook" || social === "apple" || social === "twitter" || social === "discord" || social === "email")
                          ? "text-white"
                          : theme === "color" && (social === "apple" || social === "twitter")
                            ? "text-alpha-black"
                            : "",
                )}
                colorful={
                    (theme === "brand" && (social === "google" || social === "figma")) ||
                    (theme === "color" && (social === "google" || social === "facebook" || social === "figma" || social === "dribble" || social === "discord")) ||
                    undefined
                }
            />

            {children}
        </Component>
    );
};
